import type { HTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

export function Skeleton({ className, ...props }: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      className={cn('animate-pulse rounded-md bg-slate-200/80 dark:bg-slate-800/80', className)}
      {...props}
    />
  );
}

// Preset helper components for common loading layouts
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn(
            'h-3.5',
            i === lines - 1 ? 'w-3/5' : i === 0 ? 'w-full' : 'w-4/5',
          )}
        />
      ))}
    </div>
  );
}

export function SkeletonStatCard({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'flex w-full flex-col justify-between rounded-card border border-line bg-white p-4 shadow-card dark:border-slate-800 dark:bg-slate-900',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="w-full space-y-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-7 w-24" />
        </div>
        <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
      </div>
      <div className="mt-3 pt-2 border-t border-line/50 dark:border-slate-800/50">
        <Skeleton className="h-2.5 w-16" />
      </div>
    </div>
  );
}

export function SkeletonTableRow({ cols = 5, className }: { cols?: number; className?: string }) {
  return (
    <tr className={cn('border-b border-line dark:border-slate-800', className)}>
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className="px-4 py-3.5">
          <Skeleton className={cn('h-4', i === 0 ? 'w-32' : i === cols - 1 ? 'w-16 ms-auto' : 'w-24')} />
        </td>
      ))}
    </tr>
  );
}

export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'flex flex-col justify-between rounded-card border border-line bg-white p-5 shadow-card dark:border-slate-800 dark:bg-slate-900',
        className,
      )}
    >
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-12 rounded-full" />
        </div>
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-2/3" />
      </div>
      <div className="mt-4 pt-3 flex items-center justify-between border-t border-line/60 dark:border-slate-800/60">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-6 w-16 rounded-lg" />
      </div>
    </div>
  );
}

Skeleton.Text = SkeletonText;
Skeleton.StatCard = SkeletonStatCard;
Skeleton.TableRow = SkeletonTableRow;
Skeleton.Card = SkeletonCard;

export default Skeleton;
